import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

const TokenContext = createContext();

export const useToken = () => {
  const context = useContext(TokenContext);
  if (context === undefined) {
    throw new Error('useToken must be used within a TokenProvider');
  }
  return context;
};

export const TokenProvider = ({ children }) => {
  const [selectedToken, setSelectedToken] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();

  // Extract token ID from URL params or query params
  useEffect(() => {
    let tokenId = params.tokenId || searchParams.get('token');
    
    // Fallback: extract tokenId from pathname if params don't work
    if (!tokenId && location.pathname) {
      const pathMatch = location.pathname.match(/\/(dashboard|ai-trading|wallets|solana-distribution|token-distribution|payments)\/([^\/]+)/);
      if (pathMatch) {
        tokenId = pathMatch[2];
      }
    }
    
    // Debug logging
    console.log('🔴 [TokenContext] URL DEBUGGING:', {
      paramsTokenId: params.tokenId,
      searchParamsToken: searchParams.get('token'),
      pathnameExtracted: location.pathname.match(/\/(dashboard|ai-trading|wallets|solana-distribution|token-distribution|payments)\/([^\/]+)/)?.[2],
      finalTokenId: tokenId,
      pathname: location.pathname,
      locationState: location.state,
      allParams: params,
      fullUrl: window.location.href
    });
    
    if (tokenId && tokenId !== selectedToken?.id) {
      // If we have token data from navigation state, use it
      if (location.state?.tokenData) {
        const token = location.state.tokenData;
        console.log('TokenContext: Setting token from location state:', token);
        setSelectedToken(token);
        setTokenData(token);
      } else if (tokenId) {
        // Otherwise just set the token ID and let components load the data
        console.log('TokenContext: Setting token ID only:', tokenId);
        setSelectedToken({ id: tokenId });
        setTokenData(null);
      }
    } else if (!tokenId) {
      // Clear token when not on a token-specific route
      console.log('TokenContext: Clearing token - no tokenId found');
      setSelectedToken(null);
      setTokenData(null);
    }
  }, [location, params.tokenId, searchParams, selectedToken?.id]);

  const selectToken = (token) => {
    setSelectedToken(token);
    setTokenData(token);
  };

  const clearToken = () => {
    setSelectedToken(null);
    setTokenData(null);
  };

  const updateTokenData = (data) => {
    setTokenData(data);
  };

  const value = {
    selectedToken,
    tokenData,
    selectToken,
    clearToken,
    updateTokenData,
    // Helper to check if we're in a token context
    hasToken: !!selectedToken,
    tokenId: selectedToken?.id || null
  };

  // Debug logging
  console.log('TokenContext values:', {
    selectedToken,
    hasToken: !!selectedToken,
    tokenId: selectedToken?.id || null,
    pathname: location.pathname,
    searchParamsToken: searchParams.get('token')
  });

  return (
    <TokenContext.Provider value={value}>
      {children}
    </TokenContext.Provider>
  );
};

export default TokenContext;